<script lang="ts">
  // The snapshot a comment was left on ("on snapshot 3 · 1a2b3c4 ↗"),
  // linking to the Files view at that snapshot (`snapshotHref`): its diff
  // from base, at the comment's file and lines there, with its thread
  // shown (a reply whose thread has no lines there: the thread's own
  // snapshot and lines); and "outdated" when given a basis that says so.
  import type { PrStore } from '../state/pr.svelte';
  import { commentSnapshot, isOutdated, type OutdatedBasis } from '../logic/timeline';
  import { snapshotHref, type ChipTarget } from '../logic/snapshotLink';
  import { nav } from '../nav.svelte';

  interface Props {
    pr: PrStore;
    /** The commit the comment was left on, when it names one (see `commentSnapshot`). */
    commit?: string | null;
    /** When the comment was written. */
    at: string;
    /** The file the link opens at the snapshot. */
    path?: string | null;
    /** The new-side line the link opens at, when the comment names one. */
    line?: number | null;
    /**
     * Where the link opens for a comment on snapshot `sha`: a snapshot and
     * the lines there (a review thread's, see `chipTarget`), which may be
     * the snapshot the thread was written on; given, it wins over `line`.
     */
    place?: ((sha: string, head: string) => ChipTarget) | null;
    /** The review thread the link shows (by node ID). */
    thread?: string | null;
    /** The comment of `thread` the link scrolls to (by node ID). */
    comment?: string | null;
    /** What decides the outdated mark; without one the chip carries none. */
    outdated?: OutdatedBasis | null;
    /** A link to the Files view (false: a plain chip, e.g. in a dialog the link would navigate behind). */
    link?: boolean;
  }
  let {
    pr,
    commit = null,
    at,
    path = null,
    line = null,
    place = null,
    thread = null,
    comment = null,
    outdated: basis = null,
    link = true,
  }: Props = $props();

  const on = $derived(commentSnapshot(pr.snapshots, { commit, at }));
  const head = $derived(pr.pr?.headRefOid ?? '');
  const outdated = $derived(!!basis && !!on && !!head && isOutdated(on, head, basis));
  const target = $derived.by(() => {
    if (!on) return null;
    if (place) return place(on.sha, head);
    return { sha: on.sha, spot: line ? { side: 'RIGHT' as const, start: line, end: line } : null };
  });
  /** The snapshot the link opens, when it is not the one the chip names. */
  const elsewhere = $derived(
    on && target && target.sha !== on.sha ? commentSnapshot(pr.snapshots, { commit: target.sha, at }) : null,
  );
  const href = $derived(
    on && target ? snapshotHref(pr, { sha: target.sha, head, path, lines: target.spot, thread, comment }, nav.query) : '',
  );
  const linkTitle = $derived.by(() => {
    if (!on) return '';
    if (elsewhere)
      return `Open the Files view at ${elsewhere.n === null ? elsewhere.short : `snapshot ${elsewhere.n} (base → ${elsewhere.short})`}, where this thread was written`;
    return on.n === null ? `Open the Files view at ${on.short}` : `Open the Files view at snapshot ${on.n} (base → ${on.short})`;
  });
</script>

{#if on && !link}
  <span class="anchor-chip snap-chip" title={on.n === null ? `Left on ${on.short}` : `Left on snapshot ${on.n} (base → ${on.short})`}
    >on {on.n === null ? '' : `snapshot ${on.n} · `}{on.short}</span
  >
  {#if outdated}<span class="badge outdated" title="Left on an earlier snapshot than the head">outdated</span>{/if}
{:else if on}
  <a
    class="anchor-chip snap-chip"
    {href}
    title={linkTitle}
    >on {on.n === null ? '' : `snapshot ${on.n} · `}{on.short} ↗</a
  >
  {#if outdated}<span class="badge outdated" title="Left on an earlier snapshot than the head">outdated</span>{/if}
{/if}
